import { useMemo } from 'react';
import { resolveIndexThemeId } from '@apps/card/utils/colorway';
import type { ThemeEmbeddings } from '@shared/utils/search';
import { usePrecomputedEmbeddings } from '@shared/utils/search';
import { COLOR_FACET, STYLE_FACET } from './Filters.constants';
import type { StyleKeywordGroups } from './facetOptions.types';

/**
 * The Style and Color vocabularies are derived from the theme-search embeddings
 * index — the same rows search scores against — rather than from the per-template
 * `stationeryTemplate.tags` word list, which the catalogue no longer keeps clean.
 *
 * A keyword sitting in any row's concept slot is a Style option; one in a color
 * slot is a Color option. Nothing else is configured anywhere: add a keyword to
 * the index and the filter offers it.
 */

/**
 * Index keywords carry a role namespace on some entries (`color: roseGold`,
 * `material: marble`) and pack search synonyms into one string
 * (`botanical leaves`, `illustration hand-drawn conceptual`).
 */
const KEYWORD_NAMESPACE_RE = /^\w+: /;

/** A keyword namespaced as a palette word, wherever in the row it appears. */
const COLOR_KEYWORD_RE = /^color: /;

/** The keyword minus its role namespace — the part that is actually about the design. */
const bareKeyword = (keyword: string): string => keyword.replace(KEYWORD_NAMESPACE_RE, '');

/**
 * The option *name* for a keyword: its first bare word.
 *
 * This is the half that appears in gallery URLs, so it is what keeps the live
 * expanded-gallery nav's deep links working — `botanical leaves` →
 * `style:botanical`, `minimal simple` → `style:minimal`, `typographic script` →
 * `style:typographic`. Multi-word keywords are search expansions, never option
 * names.
 */
const KEYWORD_OPTION_NAME: ReadonlyMap<string, string> = new Map([
  // Namespaced without the colon the strip above expects: the word is `oval`,
  // and `shape` is the role. Taking the first word would offer "Shape".
  ['shape oval', 'oval'],
]);

export const facetOptionNameForKeyword = (keyword: string): string => KEYWORD_OPTION_NAME.get(keyword) ?? bareKeyword(keyword).split(' ')[0];

/**
 * Concept keywords that describe how a design is *merchandised*, not what it looks
 * like, so they are never Style options. They sit in the concept slot of the index
 * today and are on their way to the customs column; until then the filter has to
 * know the difference, or the Style rail offers "Popular" between Vintage and
 * Whimsical. Search still scores them, which is where they earn their keep.
 */
const MERCHANDISING_KEYWORDS: ReadonlySet<string> = new Set(['popular', 'top']);

/**
 * A keyword's display name: the option name with camelCase split and each word
 * capitalised, so `artDeco` reads "Art Deco" and `botanical leaves` reads
 * "Botanical". The index keeps search strings, not shopper-facing copy, and the
 * labels that used to come from the tag definitions table left with it. A locale
 * entry still wins over this where one exists.
 */
const CURATED_LABEL: ReadonlyMap<string, string> = new Map([
  // The one label the derivation cannot reproduce. Checked against the 61 prod tag
  // definitions: every other curated label is the title-cased name, and `blackWhite`
  // ("Black & White") is not an index keyword so never reaches a filter.
  ['paint', 'Painted'],
]);

export function facetOptionLabelForName(name: string): string {
  const curated = CURATED_LABEL.get(name);
  if (curated) return curated;
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The `color:<name>` facet option for a color name classified by the
 * theme-search embeddings index, or `undefined` for a slot that holds no color
 * (the loader normalises a non-color keyword such as `colorful` to '').
 *
 * Palette names are single tokens by construction and are used verbatim, which
 * is what keeps `color:roseGold` — the name the gallery URL has always carried —
 * stable.
 */
export function colorFacetOptionForColorName(colorName: string) {
  return colorName ? `${COLOR_FACET}:${colorName}` : undefined;
}

/** The `style:<name>` facet option for an embeddings-index concept keyword. */
export function styleFacetOptionForConceptName(conceptName: string) {
  if (MERCHANDISING_KEYWORDS.has(conceptName)) return undefined;
  // Some rows carry `color: beige` in a concept slot. It is a palette word wherever
  // it sits, and the Colors facet already offers it, so it is never a Style.
  if (COLOR_KEYWORD_RE.test(conceptName)) return undefined;
  const name = facetOptionNameForKeyword(conceptName);
  return name ? `${STYLE_FACET}:${name}` : undefined;
}

/**
 * Every Style and Color option the index can offer, with a display name for each
 * (`facetOptionLabelForName`). `useFilterFacets` prefers its locale map and only
 * falls back here.
 */
export type FacetOptionVocabulary = Readonly<{
  /** Composite code → its fallback label. */
  labelByOption: ReadonlyMap<string, string>;
}>;

const EMPTY_VOCABULARY: FacetOptionVocabulary = { labelByOption: new Map() };

/** Keyed on the loaded index, which is a module-level singleton, so the scan runs once per bundle. */
const vocabularyByIndex = new WeakMap<ReadonlyMap<string, ThemeEmbeddings>, FacetOptionVocabulary>();

/**
 * The filter vocabularies, scanned off the index rows. Empty until the bundle
 * loads, at which point the Style and Color facets have options to offer.
 *
 * Keywords are taken in sorted order so a first-word collision resolves the same
 * way every time: `typographic` and `typographic script` are both `style:typographic`,
 * and the label is the first of the two.
 */
export function facetOptionsFromIndex(themes: ReadonlyMap<string, ThemeEmbeddings> | undefined): FacetOptionVocabulary {
  if (!themes) return EMPTY_VOCABULARY;
  const cached = vocabularyByIndex.get(themes);
  if (cached) return cached;

  const conceptKeywords = new Set<string>();
  const colorNames = new Set<string>();
  for (const row of themes.values()) {
    for (const keyword of row.conceptNames) if (keyword) conceptKeywords.add(keyword);
    // A color slot holding a non-color keyword (the index also emits `colorful`)
    // is already normalised to '' by the loader, so it never becomes an option.
    for (const colorName of row.colorNames) if (colorName) colorNames.add(colorName);
  }

  const labelByOption = new Map<string, string>();
  const label = (option: string | undefined, name: string) => {
    if (option && !labelByOption.has(option)) labelByOption.set(option, facetOptionLabelForName(name));
  };

  for (const keyword of [...conceptKeywords].sort()) label(styleFacetOptionForConceptName(keyword), facetOptionNameForKeyword(keyword));
  for (const colorName of [...colorNames].sort()) label(colorFacetOptionForColorName(colorName), colorName);

  const vocabulary: FacetOptionVocabulary = { labelByOption };
  vocabularyByIndex.set(themes, vocabulary);
  return vocabulary;
}

/** The filter vocabularies for the loaded index. */
export function useFacetOptions(): FacetOptionVocabulary {
  const { themes } = usePrecomputedEmbeddings();
  return useMemo(() => facetOptionsFromIndex(themes), [themes]);
}

/** The index-derived fallback label for a composite code, if it has one. */
export function mapToLabel(facetOption: string, labelByOption: ReadonlyMap<string, string>) {
  return labelByOption.get(facetOption);
}

/**
 * The `style:*` options a template offers.
 *
 * Two kinds, both read off the same index row:
 * - **Group options** (`style:bold`) — the ones the Style rail offers. A template is in a group
 *   when one of its concepts is the group key itself or any of the group's member keywords, so
 *   `style:bold` is the union of bold, geometric, bright and typographic. A key that is not a
 *   keyword (a combined key such as `floralAndBotanical`) simply never matches a concept itself.
 * - **Keyword options** (`style:disco`) — not offered in the rail (`useFilterFacets`),
 *   but still matched so the live nav deep links keep filtering (`NAV_STYLE_SLUGS` in the spec).
 *   Where a group key is also a keyword (`classic`, `rustic`) the two share one code, and the
 *   group's wider meaning is what it matches.
 *
 * The one place the gallery turns a template into style options, so the filter and the counts
 * cannot disagree about which row it reads.
 * Base row only — the index is color-neutral, so do not add colorway traversal here.
 *
 * The row must actually carry concepts: a suite card indexed with an empty concept
 * list (`gold_band_foil`) would otherwise stop the rollup dead instead of falling
 * through to the parent that describes the artwork.
 */
export function styleOptionsForTemplate(themeId: string, themes: ReadonlyMap<string, ThemeEmbeddings> | undefined, keywordGroups: StyleKeywordGroups = {}): string[] {
  if (!themes) return [];
  const indexThemeId = resolveIndexThemeId(themeId, key => !!themes.get(key)?.conceptNames.length);
  if (!indexThemeId) return [];
  const conceptNames = themes.get(indexThemeId)?.conceptNames ?? [];
  const options: string[] = [];
  for (const [groupKey, members] of Object.entries(keywordGroups)) {
    if (conceptNames.includes(groupKey) || members.some(member => conceptNames.includes(member))) options.push(`${STYLE_FACET}:${groupKey}`);
  }
  for (const conceptName of conceptNames) {
    const option = styleFacetOptionForConceptName(conceptName);
    if (option && !options.includes(option)) options.push(option);
  }
  return options;
}
